import { ClientSidebar } from '@ecosystem/newron-client-lib';
import { actions, containers, elements } from '@ecosystem/newron-design-system';
import { FilterTrayTabId as FilterTrayTabIdEnum } from '@ecosystem/newron-design-system/build/exports/containers';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCorePageState } from '../../../page/provider';
import { PageSettingsManager } from '../../../page/settings';
import {
  CustomFeatureDetails,
  Entity,
  EventElementCategory,
  EventElementType,
  EventName,
  EventParams,
  FilterDefinition,
  FilterSections,
  FilterType,
  Params,
  PreferenceName,
} from '../../../types';
import {
  CustomCategoricalClass,
  CustomNumericalClass,
  SavedCustomFeatureSegments,
  SharePermissionsBody,
} from '../../../types/content/custom.types';
import { FilterEntry } from '../../filterEntry';
import { useSidebar } from '../useSideBar.hook';
import { CustomSegmentType } from '../../types';
import { NumericFeature } from './renderNumericFeature';
import { useAnalyticsContext } from '../../../analytics/analytics.provider';
import { CategoricalFeature } from './renderCategoricalFeature';
import { useSaveFeatureSegments } from '../../saveFeatureSegments.hook';
import { SavedCustomSegmentsCard } from '../savedCustomSegmentsCard';
import { SavedCustomSegmentsModal } from '../saveCustomSegmentsModal';
import {
  formatFeatureClassForParams,
  FormatFeatureClassInput,
  getFeatureClassesFromSettings,
  getSharedUsers,
  shareToUser,
  validateLabelInput,
  validateNumberInput,
} from '../../utils';
import { ShareCustomModal } from '../../shareCustomModal';
import { userClient } from '../../../client/user.client';
import { getCurrentActiveComponentNameForCustomTray, isDiffSavedFeatures } from '../customSegmentsUtils';
import { ContextManager } from '../../../context/manager';
import { SelectedCustomFeatureContext } from '../../../common/selection.menu/types';
import { SEGMENT_CUSTOM_FEATURE } from '../../../common/selection.menu/constants';

const { Button, ButtonGroup, IconButton } = actions;
const { Icon } = elements;
const { FilterTrayTabId, Tray } = containers;

type ComponentProps = {
  sections: FilterSections[];
  updateSettings: (updates: Params) => void;
  contextCustomFeature?: CustomFeatureDetails;
};

function FeatureEditTrayComponent({ sections, updateSettings, contextCustomFeature }: ComponentProps) {
  const { setCustomizationTray, customizationTrayTabId, dsl, page } = useCorePageState();
  const [showSaveCustomFeatureModal, setShowSaveCustomFeatureModal] = useState(false);
  const currentActiveComponentName = getCurrentActiveComponentNameForCustomTray(dsl.sections);
  const componentSettings = PageSettingsManager.getInstance().getComponentSettings(currentActiveComponentName);
  const [defaultFeatureClass, setDefaultFeatureClass] = useState<CustomNumericalClass | CustomCategoricalClass>(null);
  const [featureClasses, setFeatureClasses] = useState<CustomNumericalClass[] | CustomCategoricalClass[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<FilterDefinition>(null);
  const [activeTab, setActiveTab] = useState<FilterTrayTabIdEnum>(customizationTrayTabId || FilterTrayTabId.Configure);
  const { trackPageEvent } = useAnalyticsContext();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [featureToShare, setFeatureToShare] = useState<SavedCustomFeatureSegments>();
  const { secondSegmentationConfig, originMenu } = contextCustomFeature || {};

  const {
    applicableSavedFeatureSegments,
    unapplicableSavedFeatureSegments,
    saveCustomFeatureSegments,
    deleteSavedCustomFeatureSegments,
  } = useSaveFeatureSegments();

  const shouldCheckForDuplicates = (preventDuplicatesWith: string[]) => {
    return preventDuplicatesWith && !preventDuplicatesWith.includes(originMenu);
  };

  const filterSavedFeatureSegments = () => {
    const { valuesToPrevent, preventDuplicatesWith } = secondSegmentationConfig;

    if (!valuesToPrevent?.length) return;

    const unapplicableBySecondSeg = [];
    const applicableBySecondSeg = applicableSavedFeatureSegments.filter(savedOption => {
      if (!preventDuplicatesWith) return true;

      for (const menuKey of preventDuplicatesWith) {
        const currentSettings = PageSettingsManager.getInstance().getComponentSettings(currentActiveComponentName);
        const otherMenuValues = currentSettings?.[menuKey];

        if (!otherMenuValues?.length) continue;

        const otherMenuValue = otherMenuValues[0];

        const savedFeatureBase = savedOption.segmentationName.split('_id')[0];
        const isOtherMenuUsingCustomFeature = otherMenuValue.includes(SEGMENT_CUSTOM_FEATURE);

        if (isOtherMenuUsingCustomFeature) {
          const hasConflict = valuesToPrevent.some(
            value =>
              savedOption.segmentationName.startsWith(value) || savedOption.segmentationName === value.split('_')[0]
          );

          if (hasConflict) {
            unapplicableBySecondSeg.push(savedOption);
            return false;
          }
        } else {
          const standardFeatureBase = otherMenuValue.split('_id')[0];

          if (savedFeatureBase === standardFeatureBase) {
            unapplicableBySecondSeg.push(savedOption);
            return false;
          }
        }
      }

      return true;
    });

    if (unapplicableBySecondSeg.length > 0) {
      if (isDiffSavedFeatures(applicableBySecondSeg, applicableSavedFeatures)) {
        setApplicableSavedFeatures(applicableBySecondSeg);
      }

      const newUnapplicable = [...unapplicableSavedFeatureSegments, ...unapplicableBySecondSeg];
      if (isDiffSavedFeatures(newUnapplicable, unapplicableSavedFeatures)) {
        setUnapplicableSavedFeatures(newUnapplicable);
      }
    } else {
      setPureApplicableAndNonApplicableSavedFeatures();
    }
  };

  const needApplicableFilter = () => {
    if (!secondSegmentationConfig) return false;

    const { valuesToPrevent, preventDuplicatesWith } = secondSegmentationConfig;
    if (!shouldCheckForDuplicates(preventDuplicatesWith) || valuesToPrevent?.length === 0) return false;

    return true;
  };

  const setPureApplicableAndNonApplicableSavedFeatures = () => {
    const shouldUpdateApplicableSavedFeatures = isDiffSavedFeatures(
      applicableSavedFeatureSegments,
      applicableSavedFeatures
    );
    const shouldUpdateUnapplicableSavedFeatures = isDiffSavedFeatures(
      unapplicableSavedFeatureSegments,
      unapplicableSavedFeatures
    );
    if (shouldUpdateApplicableSavedFeatures) {
      setApplicableSavedFeatures(applicableSavedFeatureSegments);
    }
    if (shouldUpdateUnapplicableSavedFeatures) {
      setUnapplicableSavedFeatures(unapplicableSavedFeatureSegments);
    }
  };

  const [applicableSavedFeatures, setApplicableSavedFeatures] = useState(applicableSavedFeatureSegments);
  const [unapplicableSavedFeatures, setUnapplicableSavedFeatures] = useState(unapplicableSavedFeatureSegments);

  useEffect(() => {
    if (needApplicableFilter()) {
      filterSavedFeatureSegments();
    } else {
      setPureApplicableAndNonApplicableSavedFeatures();
    }
  }, [applicableSavedFeatureSegments, unapplicableSavedFeatureSegments]);

  useEffect(() => {
    const featuresSection = sections?.find(item => item?.label === 'Features');
    const hasFeatures = featuresSection && featuresSection.ungrouped?.definitions?.length > 0;

    if (!hasFeatures) {
      // Close the tray if features are not available and tray already exists
      setCustomizationTray(null);
    }
  }, [sections, setCustomizationTray]);

  const categoricalFeatureRef = useRef(null);

  useEffect(() => {
    setActiveTab(customizationTrayTabId || FilterTrayTabId.Configure);
  }, [customizationTrayTabId]);

  const { expanded, hideSidebar, openSidebar } = useSidebar('custom-feature-classes');

  //We will need to force the Custom Feature Tray to be opened once its rendered.
  useEffect(() => {
    if (!expanded) {
      openSidebar(); // Avoid multiple state changes by checking `expanded`
    }
  }, [expanded, openSidebar]);

  /**
   * Function to handle tab change
   * @param tabId the tab id
   */
  const onTabChange = (tabId: FilterTrayTabIdEnum) => {
    setActiveTab(tabId);
  };

  /**
   * Function to handle base feature change
   * This is the internal function to allow calling with `baseFeatures`.
   * @param entity - The selected entity
   * @param features - The features list (optional)
   */
  const handleBaseFeatureChange = (entity: Entity, features?: FilterDefinition[]) => {
    if (!entity) {
      setSelectedFeature(null);
      return;
    }

    // Use provided features or the current `baseFeatures` state
    const featureList = features || baseFeatures;
    let baseFeature = featureList?.find(item => item.name === entity.value);

    baseFeature = { ...baseFeature, value: baseFeature.name };
    setSelectedFeature(baseFeature);

    if (baseFeature?.type === FilterType.numericRange) {
      const initialFeatureClass: CustomNumericalClass = {
        label: '',
        min: 0,
        max: null,
      };
      setDefaultFeatureClass(initialFeatureClass);
      setFeatureClasses([initialFeatureClass]);
    } else {
      const initialFeatureClass: CustomCategoricalClass = {
        label: '',
        selectedOptions: [],
        options: baseFeature.options,
      };
      if (categoricalFeatureRef.current) {
        categoricalFeatureRef.current.setTotalAvailableCategories(baseFeature.options.length);
      }
      setDefaultFeatureClass(initialFeatureClass);
      setFeatureClasses([initialFeatureClass]);
    }
  };

  const [baseFeatures, options] = useMemo(() => {
    let ungroupedFeatures = sections?.find(item => item?.label === 'Features')?.ungrouped?.definitions;

    // If features are not present, clear state and return empty arrays
    if (!ungroupedFeatures || ungroupedFeatures.length === 0) {
      setSelectedFeature(null);
      setFeatureClasses([]);
      return [[], []];
    }

    if (!contextCustomFeature?.selectedFeature) {
      // Use first feature as default
      const initialFeature = ungroupedFeatures[0];
      setSelectedFeature(initialFeature);
      handleBaseFeatureChange(
        {
          label: initialFeature.label,
          value: initialFeature.name,
        },
        ungroupedFeatures
      );
      return [
        ungroupedFeatures,
        ungroupedFeatures.map(({ label, name }) => ({
          label,
          value: name,
          isDisabled: false,
        })),
      ];
    }

    const { isDefaultState, feature, retainedFeatureClasses } = getFeatureClassesFromSettings(
      componentSettings,
      contextCustomFeature.selectedFeature,
      ungroupedFeatures,
      page,
      originMenu
    );

    const initialFeature = isDefaultState ? ungroupedFeatures[0] : feature;
    setSelectedFeature(initialFeature);
    handleBaseFeatureChange(
      {
        label: initialFeature.label,
        value: initialFeature.name,
      },
      ungroupedFeatures
    );

    if (!isDefaultState && retainedFeatureClasses) {
      setFeatureClasses(retainedFeatureClasses);
    }

    return [
      ungroupedFeatures,
      ungroupedFeatures.map(({ label, name }) => ({
        label,
        value: name,
        isDisabled: false,
      })),
    ];
  }, [sections, contextCustomFeature, originMenu, componentSettings]);

  /**
   * Function to get the filtered options based on the second segmentation configuration
   */
  const getFilteredOptions = useMemo(() => {
    if (!secondSegmentationConfig) {
      return options;
    }

    const { valuesToPrevent, preventDuplicatesWith } = secondSegmentationConfig;

    if (!shouldCheckForDuplicates(preventDuplicatesWith)) {
      return options;
    }

    if (!valuesToPrevent?.length) {
      return options;
    }

    const activeCustomFeatureValues = preventDuplicatesWith
      .filter(menuKey => menuKey !== originMenu)
      .filter(menuKey => {
        const otherMenuValues = componentSettings?.[menuKey];
        return otherMenuValues?.length > 0 && otherMenuValues[0].includes(SEGMENT_CUSTOM_FEATURE);
      })
      .flatMap(() => valuesToPrevent.filter(Boolean));

    const activeStandardFeatureValues = preventDuplicatesWith
      .filter(menuKey => menuKey !== originMenu)
      .flatMap(menuKey => {
        const otherMenuValues = componentSettings?.[menuKey];
        if (!otherMenuValues?.length) return [];

        const otherMenuValue = otherMenuValues[0];

        if (otherMenuValue.includes(SEGMENT_CUSTOM_FEATURE)) {
          return [];
        }

        return [otherMenuValue];
      });

    return options.map(option => {
      // Check if this option conflicts with custom features
      const conflictsWithCustomFeature = activeCustomFeatureValues.some(
        value => option.value.startsWith(value) || `${option.value}_id` === value
      );

      // Check if this option conflicts with standard features
      const conflictsWithStandardFeature = activeStandardFeatureValues.some(standardValue => {
        const optionBase = option.value.split('_id')[0];
        const standardBase = standardValue.split('_id')[0];
        return optionBase === standardBase;
      });

      const shouldDisable = conflictsWithCustomFeature || conflictsWithStandardFeature;
      return {
        ...option,
        isDisabled: shouldDisable,
      };
    });
  }, [options, secondSegmentationConfig, originMenu, componentSettings]);

  const definitions = useMemo(
    () => ({
      type: 'select',
      name: 'Base Feature Dropdown',
      placeholder: 'Select',
      label: 'Base Feature',
      options: getFilteredOptions,
    }),
    [getFilteredOptions]
  );

  const areFeatureClassesValid = () => {
    const areLabelsValid = validateLabelInput(featureClasses);
    if (!areLabelsValid) {
      return false;
    }

    if (selectedFeature.type === FilterType.numericRange) {
      const areInputsValid = validateNumberInput(featureClasses as CustomNumericalClass[]);
      return areInputsValid;
    }

    if (selectedFeature.type === FilterType.multiSelect) {
      const { hasNoSelectedOptions, hasNoLabel } = validateCategoricalFeatureClasses();
      if (categoricalFeatureRef.current) {
        categoricalFeatureRef.current.setEmptySelectedOption(hasNoSelectedOptions);
        categoricalFeatureRef.current.setEmptyLabel(hasNoLabel);
      }
      if (hasNoLabel.length > 0 || hasNoSelectedOptions.length > 0) {
        return false;
      }
      return true;
    }

    return false;
  };

  const prepareUpdatedTiers = (
    validOriginMenu: string,
    formattedTiers: string,
    currentSettings: {
      [x: string]: string[];
    }
  ) => {
    // Get existing customFeatureTiers and update with current menu's tier
    let existingTiers = currentSettings.customFeatureTiers || [];

    // Remove any existing tier for this menu
    existingTiers = existingTiers.filter(tier => !tier.startsWith(`${validOriginMenu}:`) && !tier.startsWith('name:'));

    // Add the new tier with menu prefix
    const menuPrefixedTier = `${validOriginMenu}:${formattedTiers}`;
    existingTiers.push(menuPrefixedTier);

    return existingTiers;
  };

  const handleApply = () => {
    if (!areFeatureClassesValid()) return;
    const input: FormatFeatureClassInput = {
      selectedFeatureName: selectedFeature.name,
      selectedFeatureType: selectedFeature.type,
      segmentClasses: featureClasses,
    };
    const formattedTiers = formatFeatureClassForParams(input);

    const customFeatureContext: SelectedCustomFeatureContext = {
      type: selectedFeature.type,
      label: selectedFeature.label,
      value: selectedFeature.value,
    };

    const validOriginMenu = originMenu && originMenu !== 'undefined' ? originMenu : null;

    if (validOriginMenu) {
      ContextManager.getInstance().updateCustomFeatureValues({
        customFeature: {
          [page.name]: {
            [validOriginMenu]: {
              selectedFeature: customFeatureContext,
              originMenu: validOriginMenu,
              customFeatureType: contextCustomFeature?.customFeatureType,
              secondSegmentationConfig: secondSegmentationConfig,
            },
          },
        },
      });
    }

    const currentSettings = { ...componentSettings };
    const existingTiers = prepareUpdatedTiers(validOriginMenu, formattedTiers, currentSettings);

    const settingsUpdate = {
      ...currentSettings,
      customFeatureTiers: existingTiers,
      [validOriginMenu]: [contextCustomFeature?.customFeatureType],
    };

    updateSettings(settingsUpdate);
  };

  const isCardActive = (savedCustomSeg: SavedCustomFeatureSegments) => {
    // Check if the rendered saved custom segmentation is the same as the current applied custom feature tiers
    const input: FormatFeatureClassInput = {
      selectedFeatureName: savedCustomSeg.segmentationName,
      selectedFeatureType: savedCustomSeg.filterType,
      segmentClasses: savedCustomSeg.customSegmentation,
    };
    const formattedTier = formatFeatureClassForParams(input);
    const menuPrefixedTier = `${originMenu}:${formattedTier}`;

    const isActiveForCurrentMenu = componentSettings?.customFeatureTiers?.some(tier => tier === menuPrefixedTier);

    const currentMenuValues = componentSettings?.[originMenu];
    const isCurrentMenuUsingSavedCustomSeg =
      currentMenuValues && currentMenuValues.length > 0 && currentMenuValues[0].includes(SEGMENT_CUSTOM_FEATURE);

    return isActiveForCurrentMenu && isCurrentMenuUsingSavedCustomSeg;
  };

  /**
   * A currying function to render the saved custom segmentation card
   * @param isApplicable - whether the saved custom segmentation is applicable or not
   * @returns The function to render the saved custom segmentation card
   */
  const renderSavedCustomSegmentsFn =
    (isApplicable: boolean) => (savedCustomSeg: SavedCustomFeatureSegments, index: number) => {
      return (
        <SavedCustomSegmentsCard
          key={`saved-custom-segments-${savedCustomSeg.name}-${index}`}
          customSegmentType={CustomSegmentType.customFeatureSegmentation}
          savedCardLabels={[
            baseFeatures?.find(item => item.name === savedCustomSeg.segmentationName)?.label ??
              savedCustomSeg.segmentationLabel,
          ]}
          savedData={savedCustomSeg}
          isActive={isCardActive(savedCustomSeg)}
          isApplicable={isApplicable}
          handleApply={handleApplySavedCustomSegmentation}
          handleDelete={handleDeleteSavedCustomSegmentation}
          handleShare={hanldeShareSavedCustomSegmentation}
        />
      );
    };
  const recordAnalyticEventDetails = (
    event: string,
    category: EventElementCategory,
    featureClassesList: CustomNumericalClass[] | CustomCategoricalClass[],
    filterType?: string,
    label?: string
  ) => {
    let params: EventParams = {
      elementType: EventElementType.Button,
      elementCategory: category,
      elementName: event,
    };

    if (category === EventElementCategory.CustomFeatureFilter) {
      params.featuresGroup = label ?? selectedFeature.label;
    }

    let list: string[] = [];

    // Process featureClassesList if it exists and has elements
    if (featureClassesList && featureClassesList.length > 0 && filterType) {
      if (filterType === FilterType.numericRange) {
        // Handle numeric range - Directly push min and max values
        featureClassesList.forEach(featureClass => {
          list.push(featureClass.min.toString());
        });
      } else {
        // Handle categorical options
        const catFeatures = featureClassesList as CustomCategoricalClass[];

        let keys = catFeatures.filter(item => item.selectedOptions).flatMap(item => item.selectedOptions);
        const flattenedkeys = keys.flatMap(item => item.split(','));
        list = getOptionsList(flattenedkeys, label);
      }
    }
    // Only set customFeatureClasses if the list is populated
    if (list.length > 0) {
      params.customFeatureClasses = list;
    }
    // Track the page event with the prepared parameters
    trackPageEvent(EventName.PageElementClicked, params);
  };
  const getOptionsList = (keys, label) => {
    const list: string[] = [];
    const catFeatureOptions = baseFeatures.find(item => item.label === label)?.options;
    catFeatureOptions?.map(item => {
      if (keys.includes(item.value)) {
        list.push(item.label);
      }
    });

    return list;
    // }
  };

  const onApplyFromCustomizedTab = () => {
    handleApply();
    recordAnalyticEventDetails(
      'Apply Custom Feature Classes',
      EventElementCategory.CustomFeatureFilter,
      featureClasses,
      selectedFeature.type,
      selectedFeature.label
    );
  };
  const handleApplySavedCustomSegmentation = (savedCustomSeg: SavedCustomFeatureSegments) => {
    recordAnalyticEventDetails(
      'Apply Custom Feature Classes',
      EventElementCategory.CustomFeatureFilter,
      savedCustomSeg.customSegmentation,
      savedCustomSeg.filterType,
      savedCustomSeg.segmentationLabel
    );
    const input: FormatFeatureClassInput = {
      selectedFeatureName: savedCustomSeg.segmentationName,
      selectedFeatureType: savedCustomSeg.filterType as FilterType,
      segmentClasses: savedCustomSeg.customSegmentation,
    };
    const formattedSavedCustomSeg = formatFeatureClassForParams(input);

    const validOriginMenu = originMenu && originMenu !== 'undefined' ? originMenu : null;

    if (validOriginMenu) {
      ContextManager.getInstance().updateCustomFeatureValues({
        customFeature: {
          [page.name]: {
            [validOriginMenu]: {
              selectedFeature: {
                type: savedCustomSeg.filterType,
                label: savedCustomSeg.name,
                value: savedCustomSeg.segmentationName,
                savedName: savedCustomSeg.name,
              },
              originMenu: validOriginMenu,
              customFeatureType: contextCustomFeature.customFeatureType,
              secondSegmentationConfig: secondSegmentationConfig,
            },
          },
        },
      });
    }

    const currentSettings = { ...componentSettings };
    const existingTiers = prepareUpdatedTiers(validOriginMenu, formattedSavedCustomSeg, currentSettings);

    updateSettings({
      ...currentSettings,
      customFeatureTiers: existingTiers,
      [validOriginMenu]: [`${contextCustomFeature.customFeatureType}|${savedCustomSeg.name}`],
    });
  };

  const handleDeleteSavedCustomSegmentation = (savedCustomSegToDelete: SavedCustomFeatureSegments, userId: string) => {
    deleteSavedCustomFeatureSegments(savedCustomSegToDelete, userId, originMenu);
    recordAnalyticEventDetails(
      'Delete Custom Feature Classes',
      EventElementCategory.DeleteFeatureFilter,
      savedCustomSegToDelete.customSegmentation,
      savedCustomSegToDelete.filterType,
      savedCustomSegToDelete.segmentationLabel
    );
  };

  const hanldeShareSavedCustomSegmentation = (savedCustomFeatureSegment: SavedCustomFeatureSegments) => {
    setIsShareModalOpen(true);
    setFeatureToShare(savedCustomFeatureSegment);
  };

  const validateCategoricalFeatureClasses = (): { hasNoSelectedOptions: number[]; hasNoLabel: number[] } => {
    const hasNoSelectedOptions: number[] = [];
    const hasNoLabel: number[] = [];
    featureClasses.map((fc, index) => {
      if (!fc.selectedOptions.length) {
        hasNoSelectedOptions.push(index);
      }

      if (!fc.label) {
        hasNoLabel.push(index);
      }
    });
    return { hasNoSelectedOptions, hasNoLabel };
  };

  const renderFeatureClassComponent = () => {
    if (!selectedFeature) return;

    if (selectedFeature.type === FilterType.numericRange) {
      return (
        <NumericFeature
          selectedFeature={selectedFeature}
          defaultFeatureClass={defaultFeatureClass as CustomNumericalClass}
          featureClasses={featureClasses as CustomNumericalClass[]}
          setFeatureClasses={setFeatureClasses}
        />
      );
    }
    return (
      <CategoricalFeature
        selectedFeature={selectedFeature}
        featureClasses={featureClasses as CustomCategoricalClass[]}
        setFeatureClasses={setFeatureClasses}
        ref={categoricalFeatureRef}
      />
    );
  };

  const renderSaveCustomFeatureModal = () => {
    return (
      <SavedCustomSegmentsModal
        customSegmentType={CustomSegmentType.customFeatureSegmentation}
        isOpen={showSaveCustomFeatureModal}
        existingName={[
          ...applicableSavedFeatures.map(item => item.name),
          ...unapplicableSavedFeatures.map(item => item.name),
        ]}
        onSave={handleSave}
        onClose={() => {
          setShowSaveCustomFeatureModal(false);
        }}
      />
    );
  };
  const validateAndSave = () => {
    if (!areFeatureClassesValid()) return;
    setShowSaveCustomFeatureModal(true);
  };
  const handleSave = (name: string, desc: string) => {
    recordAnalyticEventDetails(
      'Save Custom Feature Classes',
      EventElementCategory.CustomFeatureFilter,
      featureClasses,
      selectedFeature.type,
      selectedFeature.label
    );
    // recordAnalyticEventDetails('Save Custom Segmentation', priceClasses);
    //we need to extend the function here to cater for saving categorical feature
    const input: FormatFeatureClassInput = {
      selectedFeatureName: selectedFeature.name,
      selectedFeatureType: selectedFeature.type,
      segmentClasses: featureClasses,
      excludeOthers: true,
    };
    saveCustomFeatureSegments(
      name,
      desc,
      selectedFeature.label,
      formatFeatureClassForParams(input),
      selectedFeature.type,
      selectedFeature?.min,
      selectedFeature?.max
    );
    setShowSaveCustomFeatureModal(false);
  };

  const renderShareCustomFeatureModal = () => {
    return (
      <ShareCustomModal
        isOpen={isShareModalOpen}
        itemToShare={featureToShare}
        preferenceName={PreferenceName.segment_feature}
        onClose={() => setIsShareModalOpen(false)}
        onSuccess={() => {
          setIsShareModalOpen(false);
        }}
        onShareError={() => {
          setIsShareModalOpen(false);
        }}
        getUsers={userClient.getSameAccessUsers}
        getSharedUsers={getSharedUsers}
        shareToUser={onShareToUser}
      />
    );
  };

  /**
   * Function to share the feature segment to a user.
   * Called when the user clicks the "Apply changes" button in the share modal
   * @param preferenceId - the id of the preference to be shared
   * @param permissionsBody - the permissions body
   */
  const onShareToUser = async (preferenceId: string, permissionsBody: SharePermissionsBody[]) => {
    await shareToUser(preferenceId, PreferenceName.segment_feature, permissionsBody);
    recordAnalyticEventDetails('Share Custom Feature Classes', EventElementCategory.ShareFeatureFilter, null, null);
  };

  return (
    <ClientSidebar.Content>
      <Tray>
        <Tray.Header padding="medium">
          {expanded && <div className="flex-grow font-normal text-md text-heading">Feature Segments</div>}
          <IconButton
            size="md"
            variant="white"
            color="secondaryInteractive"
            onClick={() => {
              hideSidebar();
              setCustomizationTray(null);
              ContextManager.getInstance().setActiveCustomFeatureMenu(null);
            }}
            testId="feature-tray-close-btn"
          >
            <Icon.icons.Close />
          </IconButton>
        </Tray.Header>
        <Tray.Tabs currentTab={activeTab} onTabChange={onTabChange}>
          <Tray.Tabs.Tab id="tab-1">Customize</Tray.Tabs.Tab>
          <Tray.Tabs.Content id="tab-1">
            <div className="pt-4 pb-6 flex-grow-0 modal-body overflow-visible">
              <FilterEntry
                key={'custom_feature'}
                definition={definitions as FilterDefinition}
                disabled={false}
                values={[selectedFeature as Entity]}
                onChange={(_, [entity]) => handleBaseFeatureChange(entity)}
              />
              <span className="text-sm text-lighter line-truncate" style={{ marginTop: '-20px' }}>
                The feature you want to customize.
              </span>
              {renderFeatureClassComponent()}
            </div>
          </Tray.Tabs.Content>
          <Tray.Tabs.Tab id="tab-2">Saved</Tray.Tabs.Tab>
          <Tray.Tabs.Content id="tab-2">
            <div>
              <div>{applicableSavedFeatures.map(renderSavedCustomSegmentsFn(true))}</div>
              <div className="border-light border-t py-4 mt-5">
                <div className="font-bold">Unapplicable</div>
                <div className="mb-2 text-sm text-lighter">
                  Feature segments that can't be applied within the current context.
                </div>
                {unapplicableSavedFeatures.map(renderSavedCustomSegmentsFn(false))}
              </div>
            </div>
          </Tray.Tabs.Content>
        </Tray.Tabs>
        {activeTab === FilterTrayTabId.Configure && (
          <Tray.Footer>
            <div className="flex-grow">
              <ButtonGroup>
                <ButtonGroup.PrimaryButton>
                  <Button onClick={onApplyFromCustomizedTab} stretch>
                    Apply
                  </Button>
                </ButtonGroup.PrimaryButton>
                <ButtonGroup.SecondaryButtons>
                  <Button onClick={() => validateAndSave()} mode="secondary" stretch>
                    Save
                  </Button>
                </ButtonGroup.SecondaryButtons>
              </ButtonGroup>
            </div>
          </Tray.Footer>
        )}
      </Tray>
      {renderSaveCustomFeatureModal()}
      {renderShareCustomFeatureModal()}
    </ClientSidebar.Content>
  );
}

export { FeatureEditTrayComponent };
